// /src/services/api.ts

// What? A file for the API services.
// Why? To centralize API calls, promoting code reusability and simplifying data management across the application.
// How?
// - Defines custom hooks for each API endpoint, encapsulating the API call logic
// - Uses the useAuthFetch hook to make authenticated API calls
// - Returns memoized functions that execute the API requests
// - Provides type-safe interfaces for API responses using TypeScript
// - Centralizes API endpoint definitions, making it easier to manage and update
// - Allows components to easily fetch and manage data from the backend

import { useCallback, useState, useEffect } from "react";
import {
  FileType,
  User,
  PublicWorkspaceType,
  UserWorkspacesType,
  WorkspaceUser,
  WorkspaceType,
  ResourceUsageData,
  UsageTrendTimeframe,
  UsageTrendData,
  WorkspaceModuleConfigResponse,
  WorkspaceSkillOfferingRead,
  WorkspaceSkillOfferingsResponse,
  WorkspaceCustomSkillRead,
  WorkspaceCustomSkillDetail,
  FeatureFlagsResponse,
  OrganizationIntegration,
  OrganizationIntegrationCreate,
  OrganizationIntegrationDisableImpact,
  DeltekConnectionTestRead,
  OrganizationIntegrationUpdate,
  OrganizationIntegrationList,
  OrganizationIntegrationSubstrateItem,
  OrganizationIntegrationSubstrateItemCreate,
  OrganizationIntegrationSubstrateItemUpdate,
  WorkspaceIntegrationSelection,
  WorkspaceIntegrationSelectionCreate,
  WorkspaceIntegrationSelectionUpdate,
  AvailableIntegration,
  BKGAvailableSource,
  PermissionVerificationStatus,
  PermissionSyncStatusRead,
  OrgBrandingResponse,
  OrgThemeConfig,
} from "../common/types";
import { SearchBKGResults } from "../common/types";
import { useAuthFetch } from "../utils/useAuthFetch";
import { useAuthHeaders } from "../hooks/useAuthHeaders";

/*
  Mock API Instructions:
  Endpoint for returning static data in the same format as the real APIs. For easy implementation in frontend rendering.
  Ignore dynamic endpoints that add, remove, or otherwise need to modify the database.

  Naming conventions:
    API endpoint name: /api/workspace/user/list
    Function name: workspaceUserList
    Hook name: useWorkspaceUserList
*/

/*
  User APIs
*/
// What? A hook for fetching the user's workspaces.
// Why? To retrieve the list of workspaces associated with the current user.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the workspaces.
export const useUserWorkspaces = () => {
  const authFetch = useAuthFetch();

  const userWorkspaces = async (): Promise<UserWorkspacesType[]> => {
    return (await authFetch(
      `/api/user/workspaces`,
      "list workspaces",
    )) as UserWorkspacesType[];
  };
  return userWorkspaces;
};

export const usePublicWorkspaces = () => {
  const authFetch = useAuthFetch();
  return useCallback(
    async (): Promise<PublicWorkspaceType[]> =>
      (await authFetch(
        "/api/user/public-workspaces",
        "list public workspaces",
      )) as PublicWorkspaceType[],
    [authFetch],
  );
};

export const useJoinPublicWorkspace = () => {
  const authFetch = useAuthFetch();
  return useCallback(
    async (
      workspaceId: string,
    ): Promise<{ workspace_id: string; role: string; status: string }> =>
      (await authFetch(
        `/api/user/public-workspaces/${encodeURIComponent(workspaceId)}/join`,
        "join public workspace",
        { method: "POST" },
      )) as { workspace_id: string; role: string; status: string },
    [authFetch],
  );
};

/*
  Workspace APIs
*/
// What? A hook for creating a new workspace.
// Why? To add a new workspace to the user's list of workspaces.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that creates a new workspace.
export const useWorkspaceCreate = () => {
  const authFetch = useAuthFetch();

  const workspaceCreate = async (
    workspaceName: string,
    enabledModules: string[] = [],
  ): Promise<WorkspaceType> => {
    return (await authFetch(`/api/workspace/create`, "create workspace", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: workspaceName,
        enabled_modules: enabledModules,
      }),
    })) as WorkspaceType;
  };

  return workspaceCreate;
};

// What? A hook for reading a workspace by its ID.
// Why? To retrieve detailed information about a specific workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the workspace by ID.
export const useWorkspaceRead = () => {
  const authFetch = useAuthFetch();

  const workspaceRead = async (workspaceId: string): Promise<WorkspaceType> => {
    return (await authFetch(
      `/api/workspace/read?workspace_id=${workspaceId}`,
      "read workspace",
    )) as WorkspaceType;
  };

  return workspaceRead;
};

// What? A hook for getting available modules for workspace creation.
// Why? To show only actually available modules during workspace creation.

// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches available modules from backend.
export const useAvailableModules = () => {
  const authFetch = useAuthFetch();

  const getAvailableModules = useCallback(async (): Promise<{
    enabled: string[];
    config: any;
  }> => {
    return (await authFetch(
      `/api/organization/available-modules`,
      "get available modules",
    )) as { enabled: string[]; config: any };
  }, [authFetch]);

  return getAvailableModules;
};

// What? A hook for fetching workspace module configuration including feature toggles.
// Why? To get the current feature values and available feature definitions for UI rendering.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the module config.
export const useWorkspaceModuleConfig = () => {
  const authFetch = useAuthFetch();

  const getModuleConfig = useCallback(
    async (workspaceId: string): Promise<WorkspaceModuleConfigResponse> => {
      return (await authFetch(
        `/api/workspace/${workspaceId}/module-config`,
        "get workspace module config",
      )) as WorkspaceModuleConfigResponse;
    },
    [authFetch],
  );

  return getModuleConfig;
};

// What? A hook for fetching resolved feature flags for a workspace.
// Why? To allow UI layers to consume feature flags without duplicating fetch logic.
// How?
// - Uses useAuthFetch to call the backend endpoint.
// - Returns evaluated flags plus their sources and schema.
export const useWorkspaceFeatureFlags = () => {
  const authFetch = useAuthFetch();

  const getFeatureFlags = useCallback(
    async (workspaceId: string): Promise<FeatureFlagsResponse> => {
      return (await authFetch(
        `/api/workspace/${workspaceId}/feature-flags`,
        "get workspace feature flags",
      )) as FeatureFlagsResponse;
    },
    [authFetch],
  );

  return getFeatureFlags;
};

export const useWorkspaceSkillOfferings = () => {
  const authFetch = useAuthFetch();

  const getSkillOfferings = useCallback(
    async (workspaceId: string): Promise<WorkspaceSkillOfferingsResponse> => {
      return (await authFetch(
        `/api/workspace/${workspaceId}/ida/skill-offerings`,
        "fetch workspace skill offerings",
      )) as WorkspaceSkillOfferingsResponse;
    },
    [authFetch],
  );

  return getSkillOfferings;
};

export const useWorkspaceSkillOfferingToggle = () => {
  const authFetch = useAuthFetch();

  const toggleSkillOffering = useCallback(
    async (
      workspaceId: string,
      offeringKey: string,
      enabled: boolean,
      expectedVersion: number,
    ): Promise<WorkspaceSkillOfferingRead> => {
      return (await authFetch(
        `/api/workspace/${workspaceId}/ida/skill-offerings/${encodeURIComponent(
          offeringKey,
        )}`,
        "fetch workspace skill offering update",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            enabled,
            expected_version: expectedVersion,
          }),
        },
      )) as WorkspaceSkillOfferingRead;
    },
    [authFetch],
  );

  return toggleSkillOffering;
};

export const useWorkspaceCustomSkills = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string): Promise<WorkspaceCustomSkillRead[]> =>
      (await authFetch(
        `/api/workspace/${workspaceId}/ida/custom-skills`,
        "list workspace custom skills",
      )) as WorkspaceCustomSkillRead[],
    [authFetch],
  );
};

export const useWorkspaceCustomSkillDetail = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      skillId: string,
    ): Promise<WorkspaceCustomSkillDetail> =>
      (await authFetch(
        `/api/workspace/${workspaceId}/ida/custom-skills/${encodeURIComponent(skillId)}`,
        "get workspace custom skill",
      )) as WorkspaceCustomSkillDetail,
    [authFetch],
  );
};

export const useWorkspaceCustomSkillCreate = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      markdown: string,
    ): Promise<WorkspaceCustomSkillDetail> =>
      (await authFetch(
        `/api/workspace/${workspaceId}/ida/custom-skills`,
        "create workspace custom skill",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ markdown }),
        },
      )) as WorkspaceCustomSkillDetail,
    [authFetch],
  );
};

export const useWorkspaceCustomSkillUpdate = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      skillId: string,
      markdown: string,
      expectedVersion: number,
    ): Promise<WorkspaceCustomSkillDetail> =>
      (await authFetch(
        `/api/workspace/${workspaceId}/ida/custom-skills/${encodeURIComponent(skillId)}`,
        "update workspace custom skill",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ markdown, expected_version: expectedVersion }),
        },
      )) as WorkspaceCustomSkillDetail,
    [authFetch],
  );
};

export const useWorkspaceCustomSkillDelete = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      skillId: string,
    ): Promise<WorkspaceCustomSkillDetail> =>
      (await authFetch(
        `/api/workspace/${workspaceId}/ida/custom-skills/${encodeURIComponent(skillId)}`,
        "delete workspace custom skill",
        { method: "DELETE" },
      )) as WorkspaceCustomSkillDetail,
    [authFetch],
  );
};

export const useWorkspaceUpdate = () => {
  const authFetch = useAuthFetch();

  const workspaceUpdate = async (
    workspaceId: string,
    workspaceData: {
      name: string;
      description: string;
      assistant_instructions: string;
      enabled_modules?: string[];
      features?: Record<string, boolean | string | number>;
    },
  ): Promise<WorkspaceType> => {
    const body: any = {
      name: workspaceData.name,
      description: workspaceData.description,
      assistant_instructions: workspaceData.assistant_instructions,
    };

    // Only include enabled_modules if provided
    if (workspaceData.enabled_modules !== undefined) {
      body.enabled_modules = workspaceData.enabled_modules;
    }

    // Only include features if provided
    if (workspaceData.features !== undefined) {
      body.features = workspaceData.features;
    }

    return (await authFetch(
      `/api/workspace/update?workspace_id=${workspaceId}`,
      "update workspace",
      {
        method: "PUT", // PUT request for updates
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    )) as WorkspaceType;
  };

  return workspaceUpdate;
};

/**
 * Response shape from PATCH /api/workspace/settings/mode.
 *
 * Flat body — fields at root, not nested under `detail` (per the
 * post-#2241 BE error-envelope convention).
 */
export interface WorkspaceModeResponse {
  workspace_id: string;
  mode: "chat" | "agent";
  changed: boolean;
}

/**
 * Hook to toggle workspace mode between "chat" and "agent".
 *
 * Consumes the Agent-mode foundation endpoint landed in BE PR #2249
 * (cascaded to dev/stage/ssi_saas_test). Double-gated server-side
 * (env flag `ida_agent_mode` + workspace-admin-only). The FE renders
 * the toggle only for admins; on 503 (env flag disabled) the toggle
 * should hide entirely rather than expose configuration to users in
 * envs that don't have agent mode provisioned.
 *
 * Thrown errors carry `status` (HTTP) so callers can branch:
 *   - 403  `code: "agent_mode_admin_required"`   — defensive guard;
 *           shouldn't reach FE since UI gates on admin
 *   - 503  `code: "agent_mode_env_flag_disabled"` — env flag off;
 *           hide the toggle entirely
 *   - 404  `code: "workspace_not_found"`         — stale workspace id
 */
export const useUpdateWorkspaceMode = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      mode: "chat" | "agent",
    ): Promise<WorkspaceModeResponse> => {
      return (await authFetch(
        `/api/workspace/settings/mode?workspace_id=${workspaceId}`,
        "update workspace mode",
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mode }),
        },
      )) as WorkspaceModeResponse;
    },
    [authFetch],
  );
};

// What? A hook for deleting a workspace by its ID.
// Why? To remove a workspace from the user's list of workspaces.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that deletes the workspace by ID.
export const useWorkspaceDelete = () => {
  const authFetch = useAuthFetch();

  const workspaceDelete = async (workspaceId: string): Promise<void> => {
    return await authFetch(
      `/api/workspace/delete?workspace_id=${workspaceId}`,
      "delete workspace",
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  };

  return workspaceDelete;
};

export type RestorableWorkspace = {
  id: string;
  name: string;
  retention_expires_at: string;
};

export const useRestorableWorkspaces = () => {
  const authFetch = useAuthFetch();
  return async (): Promise<RestorableWorkspace[]> =>
    (await authFetch(
      "/api/workspace/restorable",
      "list restorable workspaces",
    )) as RestorableWorkspace[];
};

export const useWorkspaceRestore = () => {
  const authFetch = useAuthFetch();
  return async (workspaceId: string): Promise<void> => {
    await authFetch(
      `/api/workspace/${encodeURIComponent(workspaceId)}/restore`,
      "restore workspace",
      { method: "POST" },
    );
  };
};

/*
Workspace User APIs
*/
// What? A hook for listing users in a workspace.
// Why? To retrieve the list of users associated with a specific workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the users in the workspace.
export const useWorkspaceUserList = () => {
  const authFetch = useAuthFetch();

  const workspaceUserList = useCallback(
    async (workspaceId: string): Promise<WorkspaceUser[]> => {
      return (await authFetch(
        `/api/workspace/user/list?workspace_id=${workspaceId}`,
        "list workspace users",
      )) as WorkspaceUser[];
    },
    [authFetch],
  );

  return workspaceUserList;
};

export const useWorkspaceUserInviteSend = () => {
  const authFetch = useAuthFetch();

  const workspaceUserInviteSend = async (
    workspaceId: string,
    email: string,
    role: string,
  ) => {
    return await authFetch(
      `/api/workspace/user/invite/send?workspace_id=${workspaceId}`,
      "send user invite",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          role: role,
        }),
      },
    );
  };

  return workspaceUserInviteSend;
};

/*
  Payment APIs
*/
// What? A hook for registering a payment for a workspace.
// Why? To record a payment made by a workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that registers the payment.
export const useWorkspacePaymentRegister = () => {
  const authFetch = useAuthFetch();

  const workspacePaymentRegister = async (
    workspaceId: string,
    subscription: string,
    amount: number,
  ) => {
    return await authFetch(
      `/api/workspace/payment/register?workspace_id=${workspaceId}`,
      "register payment",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subscription: subscription,
          amount: amount,
        }),
      },
    );
  };

  return workspacePaymentRegister;
};

export const useWorkspacePaymentList = () => {
  const authFetch = useAuthFetch();

  const workspacePaymentList = async (workspaceId: string) => {
    return await authFetch(
      `/api/workspace/payment/list?workspace_id=${workspaceId}`,
      "list payments",
    );
  };

  return workspacePaymentList;
};

/*
  Workspace user permissions
*/
// What? A hook for updating the role of a user in a workspace.
// Why? To modify the permissions or roles of a user within a workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that updates the user's role in the workspace.
export const useWorkspaceUserUpdate = () => {
  const authFetch = useAuthFetch();

  const workspaceUserUpdate = async (
    workspaceId: string,
    role: string,
    userId: string,
  ) => {
    return await authFetch(
      `/api/workspace/user/update?workspace_id=${workspaceId}`,
      "update user permissions",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          role: role,
          id: userId,
        }),
      },
    );
  };

  return workspaceUserUpdate;
};

// What? A hook for removing a user from a workspace.
// Why? To remove a user from a workspace, typically when the user is no longer associated with the workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that removes the user from the workspace.
export const useWorkspaceUserRemove = () => {
  const authFetch = useAuthFetch();

  const workspaceUserRemove = async (
    workspaceId: string,
    userId: string,
  ): Promise<void> => {
    return await authFetch(
      `/api/workspace/user/remove?workspace_id=${workspaceId}`,
      "remove user from workspace",
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: userId }),
      },
    );
  };

  return workspaceUserRemove;
};

// What? A hook for accepting an invite to a workspace.
// Why? To allow a user to join a workspace after receiving an invite.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that accepts the invite to the workspace.
export const useWorkspaceUserAcceptInvite = () => {
  const authFetch = useAuthFetch();

  const workspaceUserAcceptInvite = async (
    workspaceId: string,
  ): Promise<UserWorkspacesType> => {
    return (await authFetch(
      `/api/workspace/user/invite/accept?workspace_id=${workspaceId}`,
      "accept workspace invite",
      {
        method: "POST",
      },
    )) as UserWorkspacesType;
  };

  return workspaceUserAcceptInvite;
};

/*
  User APIs
*/
// What? A hook for removing a user by their ID.
// Why? To remove a user from the system, typically when the user is no longer active or should be removed from the system.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that removes the user by ID.
export const useRemoveUser = () => {
  const authFetch = useAuthFetch();

  const removeUser = async (id: string): Promise<void> => {
    return await authFetch(`/api/user/remove`, "delete user", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id }),
    });
  };

  return removeUser;
};

// What? A hook for reading the current user's information.
// Why? To retrieve the details of the currently authenticated user.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the user's information.
export const useReadUser = () => {
  const authFetch = useAuthFetch();

  const readUser = useCallback(async (): Promise<User> => {
    return (await authFetch(`/api/user/read`, "read user")) as User;
  }, [authFetch]);

  return readUser;
};

// What? A hook for checking workspace capacity.
// Why? To determine if user can create more workspaces before showing the add modal.
// How? Calls the workspace-capacity endpoint and returns capacity info.
export interface WorkspaceCapacity {
  can_create: boolean;
  current_count: number;
  max_count: number | null;
}

export const useWorkspaceCapacity = () => {
  const authFetch = useAuthFetch();

  const getCapacity = async (): Promise<WorkspaceCapacity> => {
    return (await authFetch(
      `/api/user/workspace-capacity`,
      "check workspace capacity",
    )) as WorkspaceCapacity;
  };

  return getCapacity;
};

/*
  Tutorial hint APIs
*/
// What? Hooks for reading and writing the current user's dismissed tutorial hints.
// Why? Dismissals used to live in localStorage, which made "show once ever"
//      really mean "show once per browser" and leaked state between accounts
//      sharing a machine. Persisting per user makes the guarantee real.
// How? Standard authFetch hooks. Note the "fetch" in each tag: shouldShowRoutineToast
//      in useAuthFetch suppresses error toasts for tags containing it, which is what
//      keeps this background sync silent when it fails.
export interface TutorialHintDismissal {
  hint_id: string;
  workspace_id: string | null;
}

// Identity lookup used only to namespace tutorial hint state. Deliberately a
// separate hook from useReadUser rather than a reuse of it: the tag carries
// "fetch" so a failure stays silent like the rest of the hydration path, while
// useReadUser's "read user" tag must keep toasting for the user-facing profile
// reads. Same endpoint, different failure contract.
export const useReadUserForTutorialHints = () => {
  const authFetch = useAuthFetch();

  return useCallback(async (): Promise<User> => {
    return (await authFetch(
      `/api/user/read`,
      "fetch user for tutorial hints",
      undefined,
      undefined,
      // Background page-load call, so it opts out of the global connectivity
      // banner too. The "fetch" tag alone only silences status-code failures;
      // a blocked or offline request takes useAuthFetch's network/CORS branch,
      // which raises the banner without consulting the tag.
      { suppressNetworkBanner: true },
    )) as User;
  }, [authFetch]);
};

export const useTutorialHintDismissals = () => {
  const authFetch = useAuthFetch();

  const listDismissals = useCallback(async (): Promise<
    TutorialHintDismissal[]
  > => {
    return (await authFetch(
      `/api/user/tutorial-hints`,
      "fetch tutorial hints",
      undefined,
      undefined,
      // Background page-load call: see useReadUserForTutorialHints above.
      { suppressNetworkBanner: true },
    )) as TutorialHintDismissal[];
  }, [authFetch]);

  return listDismissals;
};

export const useDismissTutorialHint = () => {
  const authFetch = useAuthFetch();

  const dismissHint = useCallback(
    async (hintId: string, workspaceId?: string | null): Promise<void> => {
      await authFetch(
        `/api/user/tutorial-hints`,
        "fetch tutorial hint dismissal",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            hint_id: hintId,
            workspace_id: workspaceId ?? null,
          }),
        },
      );
    },
    [authFetch],
  );

  return dismissHint;
};

export const useResetTutorialHints = () => {
  const authFetch = useAuthFetch();

  // Deliberately no "fetch" in the tag: hydration and dismissal sync are
  // background work and stay silent, but this one is an explicit user action,
  // so a failure should surface as a toast rather than appearing to succeed.
  const resetHints = useCallback(async (): Promise<void> => {
    await authFetch(`/api/user/tutorial-hints`, "reset tutorial hints", {
      method: "DELETE",
    });
  }, [authFetch]);

  return resetHints;
};

/*
  File APIs
*/
// What? A hook for listing files in a workspace.
// Why? To retrieve the list of files associated with a specific workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the files in the workspace.
export const useListFiles = () => {
  const authFetch = useAuthFetch();

  const listFiles = async (workspaceId: string): Promise<FileType[]> => {
    return (await authFetch(
      `/api/workspace/file/list?workspace_id=${workspaceId}`,
      "list files",
    )) as FileType[];
  };

  return listFiles;
};

// What? A hook for deleting a file by its ID.
// Why? To remove a file from a workspace, typically when the file is no longer needed.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that deletes the file by ID.
export const useDeleteFile = () => {
  const authFetch = useAuthFetch();

  const deleteFile = async (workspaceId: string, fileId: string) => {
    return await authFetch(
      `/api/workspace/file/delete?workspace_id=${workspaceId}&file_path=${encodeURIComponent(fileId)}`,
      "delete file",
      {
        method: "DELETE",
      },
    );
  };

  return deleteFile;
};

/*
  File Upload API
*/
// What? A hook for uploading a file to a workspace.
// Why? To add a file to a workspace, typically for storing or processing.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that uploads the file to the workspace.
export const useUploadFile = () => {
  const authFetch = useAuthFetch();

  const uploadFile = async (
    workspaceId: string,
    file: File,
  ): Promise<FileType> => {
    const formData = new FormData();
    formData.append("file", file);

    return await authFetch(
      `/api/workspace/file/upload?workspace_id=${workspaceId}`,
      `upload file ${file.name}`,
      {
        method: "POST",
        body: formData,
      },
      30000,
    );
  };

  return uploadFile;
};

/*
  Get file bin
*/
// What? A hook for reading a citation from a file.
// Why? To retrieve detailed information about a specific citation from a file.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the citation from the file.
export const useWorkspaceCitationRead = () => {
  const authFetch = useAuthFetch("");

  const workspaceCitationRead = async (
    workspaceId: string,
    fileId: string,
  ): Promise<any> => {
    return await authFetch(
      `/api/workspace/citation/read?workspace_id=${workspaceId}&file_id=${encodeURIComponent(fileId)}`,
      "read file",
    );
  };

  return workspaceCitationRead;
};

export const useWorkspaceSourceFileDownload = () => {
  const authFetch = useAuthFetch("");

  const workspaceSourceFileDownload = async (
    workspaceId: string,
    fileId: string,
  ): Promise<any> => {
    return await authFetch(
      `/api/workspace/source/file/download?workspace_id=${workspaceId}&file_id=${encodeURIComponent(fileId)}`,
      "download file",
    );
  };

  return workspaceSourceFileDownload;
};

// What? A hook to retrieve resource usage data.
// Why? To display current resource usage and limits to the user.
// How? Returns a function that fetches mock resource usage data (to be replaced with actual API calls).
// TODO: Replace with actual API call.
export const useGetResourceUsage = () => {
  const authFetch = useAuthFetch();

  const getResourceUsage = async (): Promise<ResourceUsageData> => {
    const capacity: Record<string, any> = await authFetch(
      `/api/subscription/usage`,
      "retrieve subscription usage",
    );

    return {
      resources: [
        {
          name: "Workspaces",
          used: capacity["active_workspaces"],
          total: capacity["workspaces"],
          percentage: Math.round(
            (capacity["active_workspaces"] / capacity["workspaces"]) * 100,
          ),
          message: "",
        },
        {
          name: "Requests",
          used: capacity["used_requests"],
          total: capacity["monthly_requests"],
          percentage: Math.round(
            (capacity["used_requests"] / capacity["monthly_requests"]) * 100,
          ),
          message: "",
        },
        {
          name: "Storage",
          used: Math.round(capacity["used_storage"] * 100) / 100,
          total: Math.round(capacity["gb_storage"] * 100) / 100,
          percentage: Math.round(
            (capacity["used_storage"] / capacity["gb_storage"]) * 100,
          ),
          message: "",
        },
      ],
    };
  };
  return getResourceUsage;
};

// What? A hook to retrieve usage trend data.
// Why? To display historical usage trends to the user.
// How? Returns a function that fetches mock usage trend data (to be replaced with actual API calls).
// TODO: Replace with actual API call.
export const useGetUsageTrends = () => {
  const authFetch = useAuthFetch();

  const getUsageTrends = async (
    timeframe: UsageTrendTimeframe,
  ): Promise<UsageTrendData> => {
    // Function to calculate the date `days` before today and return it as a datetime string
    const calculateDateTime = (days: number): string => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return date.toISOString(); // Returns in "YYYY-MM-DDTHH:mm:ss.sssZ" format
    };

    let start_date: string;
    if (timeframe === "30") {
      start_date = calculateDateTime(30);
    } else if (timeframe === "90") {
      start_date = calculateDateTime(90);
    } else if (timeframe === "365") {
      start_date = calculateDateTime(365);
    } else {
      throw new Error("Invalid timeframe");
    }

    const end_date = new Date().toISOString();

    const data: number[] = await authFetch(
      `/api/subscription/usage-trends?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`,
      "retrieve subscription usage trends",
    );

    // Format
    const labels = data.map((_, i) => `Week ${i + 1}`);
    return {
      labels,
      datasets: [
        {
          label: "Requests Used",
          data: data,
          borderColor: "rgb(255, 111, 0)",
          backgroundColor: "rgba(255, 111, 0, 0.1)",
          tension: 0.1,
          fill: true,
        },
      ],
      options: {
        responsive: true,
        plugins: {
          legend: { position: "top" },
          title: { display: true, text: "Request Usage Over Time" },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "Requests" },
          },
          x: {
            title: { display: true, text: "Time" },
          },
        },
      },
    };
  };
  return getUsageTrends;
};

// Biotech knowledgegraph
// TODO: Temporary hooks
export const useBKGSearch = () => {
  const authFetch = useAuthFetch();

  const bkgSearch = async (
    workspaceId: string,
    params: any,
  ): Promise<SearchBKGResults> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/document/search?workspace_id=${workspaceId}`,
      "search knowledge graph",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      },
      60000,
    )) as SearchBKGResults;

    return response;
  };

  return bkgSearch;
};

export const useBKGCatalystEventSearch = () => {
  const authFetch = useAuthFetch();

  const bkgCatalystEventSearch = async (
    workspaceId: string,
    params: any,
  ): Promise<SearchBKGResults> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/document/catalyst_event?workspace_id=${workspaceId}`,
      "search knowledge graph catalyst events",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      },
      60000,
    )) as SearchBKGResults;

    return response;
  };

  return bkgCatalystEventSearch;
};

export const useBKGRead = () => {
  const authFetch = useAuthFetch();

  const bkgRead = async (
    workspaceId: string,
    indexName: string,
    documentId: string,
  ): Promise<any> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/document/get?workspace_id=${workspaceId}&index_name=${encodeURIComponent(indexName)}&document_id=${encodeURIComponent(documentId)}`,
      `retrive ${indexName} data`,
      {
        method: "GET",
      },
    )) as any;

    return response;
  };

  return bkgRead;
};

export const useBKGIngest = () => {
  const authFetch = useAuthFetch();

  const bkgRead = async (
    workspaceId: string,
    data: Record<string, any>,
  ): Promise<any> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/document/create?workspace_id=${workspaceId}`,
      `ingest data`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )) as any;

    return response;
  };

  return bkgRead;
};

export const useBKGIndexCompanyFilter = () => {
  const authFetch = useAuthFetch();

  const bkgIndexCompanyFilter = async (workspaceId: string): Promise<any> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/index/company_filter?workspace_id=${workspaceId}`,
      `get filter`,
      {
        method: "GET",
      },
    )) as any;

    return response;
  };

  return bkgIndexCompanyFilter;
};

export const useBKGIndexCatalystEventFilter = () => {
  const authFetch = useAuthFetch();

  const bkgIndexCatalystEventFilter = async (
    workspaceId: string,
  ): Promise<any> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/index/catalyst_event_filter?workspace_id=${workspaceId}`,
      `get filter`,
      {
        method: "GET",
      },
    )) as any;

    return response;
  };

  return bkgIndexCatalystEventFilter;
};

export const useBKGVCFilter = () => {
  const authFetch = useAuthFetch();

  const bkgVCFilter = async (workspaceId: string): Promise<any> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/index/venture_capital_filter?workspace_id=${workspaceId}`,
      `get venture capital filter`,
      {
        method: "GET",
      },
    )) as any;

    return response;
  };

  return bkgVCFilter;
};

type VentureCapitalIndexResponse = {
  total: number;
  limit: number;
  offset: number;
  items: any[];
};

export const useBKGVCIndex = () => {
  const authFetch = useAuthFetch();

  const bkgVCIndex = async (
    workspaceId: string,
    {
      limit,
      offset,
      filter,
      search,
      orderBy,
      orderDir,
    }: {
      limit?: number;
      offset?: number;
      filter?: Record<string, any> | null;
      search?: string;
      orderBy?: string;
      orderDir?: "asc" | "desc";
    } = {},
  ): Promise<VentureCapitalIndexResponse> => {
    const params = new URLSearchParams();

    if (limit !== undefined) params.set("limit", String(limit));
    if (offset !== undefined) params.set("offset", String(offset));
    if (search) params.set("search", search);
    if (orderBy) params.set("order_by", orderBy);
    if (orderDir) params.set("order_dir", orderDir);
    if (filter && Object.keys(filter).length > 0) {
      params.set("filter", JSON.stringify(filter));
    }

    const query = params.toString();
    const url = query
      ? `/api/workspace/bkg/autodb/index/venture_capital?workspace_id=${workspaceId}&${query}`
      : `/api/workspace/bkg/autodb/index/venture_capital?workspace_id=${workspaceId}`;

    const response = (await authFetch(url, `list venture capital`, {
      method: "GET",
    })) as VentureCapitalIndexResponse;

    return response;
  };

  return bkgVCIndex;
};

export const useBKGVCSearch = () => {
  const authFetch = useAuthFetch();

  const bkgVCSearch = async (
    workspaceId: string,
    params: any,
  ): Promise<SearchBKGResults> => {
    const response = (await authFetch(
      `/api/workspace/bkg/autodb/document/venture_capital?workspace_id=${workspaceId}`,
      `search venture capital`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      },
      60000,
    )) as SearchBKGResults;

    return response;
  };

  return bkgVCSearch;
};

/*
  Organization APIs
*/

// Create Organization
export const useCreateOrganization = () => {
  const authFetch = useAuthFetch();

  const createOrganization = async (data: {
    name: string;
    primary_domain?: string;
  }): Promise<any> => {
    return (await authFetch(`/api/organization/create`, "create organization", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })) as any;
  };

  return createOrganization;
};

// List Invitations
export const useInvitationList = () => {
  const authFetch = useAuthFetch();

  const listInvitations = async (
    orgId?: string,
    status?: string,
  ): Promise<any[]> => {
    // Always fetch from dashboard /me endpoint (which includes invitations)
    // The backend doesn't have a separate /api/organization/{orgId}/invitations endpoint
    const dashboard = (await authFetch(
      `/api/organization/me`,
      "list invitations",
    )) as any;
    let invitations =
      dashboard.pending_invitations || dashboard.invitations || [];

    // Filter by status if provided
    if (status) {
      invitations = invitations.filter((inv: any) => inv.status === status);
    }

    return invitations;
  };

  return listInvitations;
};

// Accept Invitation
export const useInvitationAccept = () => {
  const authFetch = useAuthFetch();

  const acceptInvitation = async (data: { token: string }): Promise<any> => {
    // /api/invitations, not /api/organization/*: the invitations router is
    // mounted separately in main.py because it mixes public and authenticated
    // endpoints, so it does not sit under the organization prefix.
    return (await authFetch(`/api/invitations/accept`, "accept invitation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })) as any;
  };

  return acceptInvitation;
};

// Read Organization
export const useOrganizationRead = () => {
  const authFetch = useAuthFetch();

  const readOrganization = async (): Promise<any> => {
    return (await authFetch(
      `/api/organization/me`,
      "read organization",
    )) as any;
  };

  return readOrganization;
};

// List Organization Users
export const useOrganizationUsers = () => {
  const authFetch = useAuthFetch();

  const listOrganizationUsers = async (orgId: string): Promise<any[]> => {
    const response = (await authFetch(
      `/api/organization/${orgId}/users`,
      "list organization users",
    )) as any;
    // Backend returns { users: [...] }, extract the array
    return Array.isArray(response) ? response : response.users || [];
  };

  return listOrganizationUsers;
};

// Request Access to Organization
export const useRequestAccess = () => {
  const authFetch = useAuthFetch();

  const requestAccess = async (
    orgId: string,
    data: { email?: string; message?: string },
  ): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/request-access`,
      "request access",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )) as any;
  };

  return requestAccess;
};

// Get User Usage
export const useGetUserUsage = () => {
  const authFetch = useAuthFetch();

  const getUserUsage = async (): Promise<any> => {
    return (await authFetch(
      `/api/subscription/usage`,
      "get user usage",
    )) as any;
  };

  return getUserUsage;
};

export interface QuotaStatus {
  persistentExceeded: boolean; // used_storage >= gb_storage
  periodicExceeded: boolean; // used_requests >= monthly_requests
  isLoading: boolean;
}

export const useQuotaStatus = (): QuotaStatus => {
  const getUserUsage = useGetUserUsage();
  const [status, setStatus] = useState<Omit<QuotaStatus, "isLoading">>({
    persistentExceeded: false,
    periodicExceeded: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Dev override: set localStorage flags to simulate quota-exceeded states without real data.
    // Usage (browser console):
    //   localStorage.setItem('dev_quota_storage_exceeded', '1')   → simulate full storage
    //   localStorage.setItem('dev_quota_periodic_exceeded', '1')  → simulate monthly limit hit
    //   localStorage.removeItem('dev_quota_storage_exceeded')     → reset
    if (process.env.NODE_ENV === "development") {
      const devStorage =
        localStorage.getItem("dev_quota_storage_exceeded") === "1";
      const devPeriodic =
        localStorage.getItem("dev_quota_periodic_exceeded") === "1";
      if (devStorage || devPeriodic) {
        setStatus({
          persistentExceeded: devStorage,
          periodicExceeded: devPeriodic,
        });
        setIsLoading(false);
        return;
      }
    }

    getUserUsage()
      .then((data: any) => {
        setStatus({
          persistentExceeded:
            data.gb_storage != null && data.used_storage >= data.gb_storage,
          periodicExceeded:
            data.monthly_requests != null &&
            data.used_requests >= data.monthly_requests,
        });
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...status, isLoading };
};

// Approve Access Request
export const useApproveAccessRequest = () => {
  const authFetch = useAuthFetch();

  const approveAccessRequest = async (
    orgId: string,
    data: { user_id: string; role: string },
  ): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/access-requests/approve`,
      "approve access request",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )) as any;
  };

  return approveAccessRequest;
};

// Reject Access Request
export const useRejectAccessRequest = () => {
  const authFetch = useAuthFetch();

  const rejectAccessRequest = async (
    orgId: string,
    data: { user_id: string; reason?: string },
  ): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/access-requests/reject`,
      "reject access request",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )) as any;
  };

  return rejectAccessRequest;
};

// Discover Organization by email domain
export const useDiscoverOrganization = () => {
  const authFetch = useAuthFetch();

  const discoverOrganization = async (email: string): Promise<any> => {
    return (await authFetch(
      `/api/organization/discover`,
      "discover organization",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      },
    )) as any;
  };

  return discoverOrganization;
};

// Send Invitation
export const useInvitationSend = () => {
  const authFetch = useAuthFetch();

  const sendInvitation = async (data: {
    organization_id: string;
    email: string;
    role: string;
    message?: string;
  }): Promise<any> => {
    return (await authFetch(
      `/api/organization/${data.organization_id}/invite`,
      "send invitation",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: data.email,
          role: data.role,
          message: data.message,
        }),
      },
    )) as any;
  };

  return sendInvitation;
};

// Revoke Invitation
export const useInvitationRevoke = () => {
  const authFetch = useAuthFetch();

  const revokeInvitation = async (token: string): Promise<any> => {
    return (await authFetch(
      `/api/organization/invitation/revoke?token=${token}`,
      "revoke invitation",
      {
        method: "DELETE",
      },
    )) as any;
  };

  return revokeInvitation;
};

// Delete Organization
export const useDeleteOrganization = () => {
  const authFetch = useAuthFetch();

  const deleteOrganization = async (orgId: string): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}`,
      "delete organization",
      {
        method: "DELETE",
      },
    )) as any;
  };

  return deleteOrganization;
};

// Remove User from Organization
export const useRemoveUserFromOrganization = () => {
  const authFetch = useAuthFetch();

  const removeUser = async (orgId: string, userId: string): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/members/${userId}`,
      "remove user from organization",
      {
        method: "DELETE",
      },
    )) as any;
  };

  return removeUser;
};

// Update Member Role in Organization
export const useUpdateMemberRole = () => {
  const authFetch = useAuthFetch();

  const updateMemberRole = async (
    orgId: string,
    userId: string,
    role: string,
  ): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/members/${userId}/role`,
      "update member role",
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      },
    )) as any;
  };

  return updateMemberRole;
};

/*
  Organization Domains APIs
*/

// Add Domain
export const useAddDomain = () => {
  const authFetch = useAuthFetch();

  const addDomain = async (
    orgId: string,
    data: { domain: string },
  ): Promise<any> => {
    return (await authFetch(`/api/organization/${orgId}/domain`, "add domain", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })) as any;
  };

  return addDomain;
};

// List Domains
export const useListDomains = () => {
  const authFetch = useAuthFetch();

  const listDomains = async (orgId: string): Promise<{ domains: any[] }> => {
    const response = (await authFetch(
      `/api/organization/${orgId}/domains`,
      "list domains",
    )) as any;
    // Backend returns { domains: [...] }, but handle both array and object responses
    let domainsArray = Array.isArray(response)
      ? response
      : response.domains || [];

    // Map backend field names to frontend expectations: is_verified -> verified
    domainsArray = domainsArray.map((domain: any) => ({
      ...domain,
      verified: domain.is_verified ?? domain.verified, // Support both field names for compatibility
    }));

    return { domains: domainsArray };
  };

  return listDomains;
};

// Get Verification Instructions
export const useGetVerificationInstructions = () => {
  const authFetch = useAuthFetch();

  const getVerificationInstructions = async (
    orgId: string,
    domainId: string,
  ): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/domains/${domainId}/verification-instructions`,
      "get verification instructions",
    )) as any;
  };

  return getVerificationInstructions;
};

// Verify Domain
export const useVerifyDomain = () => {
  const authFetch = useAuthFetch();

  const verifyDomain = async (
    orgId: string,
    domainId: string,
    force?: boolean,
  ): Promise<any> => {
    const params = new URLSearchParams();
    if (force) params.append("force", "true");
    const query = params.toString() ? `?${params.toString()}` : "";
    return (await authFetch(
      `/api/organization/${orgId}/domains/${domainId}/verify${query}`,
      "verify domain",
      {
        method: "POST",
      },
    )) as any;
  };

  return verifyDomain;
};

// Delete Domain
export const useDeleteDomain = () => {
  const authFetch = useAuthFetch();

  const deleteDomain = async (
    orgId: string,
    domainId: string,
  ): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/domain/${domainId}`,
      "delete domain",
      {
        method: "DELETE",
      },
    )) as any;
  };

  return deleteDomain;
};

// Update Auto Enrollment
export const useUpdateAutoEnrollment = () => {
  const authFetch = useAuthFetch();

  const updateAutoEnrollment = async (
    orgId: string,
    data: { auto_enrollment_enabled: boolean },
  ): Promise<any> => {
    return (await authFetch(
      `/api/organization/${orgId}/auto-enrollment`,
      "update auto enrollment",
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )) as any;
  };

  return updateAutoEnrollment;
};

/*
  Billing & Subscription APIs
*/

// Get Billing Packages
export const useBillingPackages = () => {
  const authFetch = useAuthFetch();

  const getBillingPackages = async (): Promise<any[]> => {
    return (await authFetch(
      `/api/billing/packages`,
      "get billing packages",
    )) as any[];
  };

  return getBillingPackages;
};

// Get Organization Subscription
export const useGetOrganizationSubscription = () => {
  const authFetch = useAuthFetch();

  const getSubscription = async (orgId: string): Promise<any> => {
    return (await authFetch(
      `/api/billing/${orgId}/subscription`,
      "get organization subscription",
    )) as any;
  };

  return getSubscription;
};

// Change Package Preview
export const useChangePackagePreview = () => {
  const authFetch = useAuthFetch();

  const changePackagePreview = async (
    orgId: string,
    newPackageId: string,
    modules?: string[],
  ): Promise<any> => {
    return (await authFetch(
      `/api/billing/${orgId}/change-package-preview`,
      "change package preview",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          new_package_id: newPackageId,
          ...(modules && modules.length > 0 ? { modules } : {}),
        }),
      },
    )) as any;
  };

  return changePackagePreview;
};

// Select Package (for initial subscription - no active subscription yet)
export const useSelectPackage = () => {
  const authFetch = useAuthFetch();

  const selectPackage = async (
    orgId: string,
    packageId: string,
    modules?: string[],
  ): Promise<any> => {
    return (await authFetch(
      `/api/billing/${orgId}/select-package`,
      "select package",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          package_id: packageId,
          enabled_modules: modules || [],
        }),
      },
    )) as any;
  };

  return selectPackage;
};

// Debug Checkout (DEV ONLY - skips Stripe payment)
export const useDebugCheckout = () => {
  const authFetch = useAuthFetch();

  const debugCheckout = async (
    orgId: string,
    packageId: string,
    modules?: string[],
  ): Promise<any> => {
    return (await authFetch(
      `/api/billing/${orgId}/debug-checkout`,
      "debug checkout",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          package_id: packageId,
          enabled_modules: modules || [],
        }),
      },
    )) as any;
  };

  return debugCheckout;
};

// Change Package (for changing existing subscription)
export const useChangePackage = () => {
  const authFetch = useAuthFetch();

  const changePackage = async (
    orgId: string,
    newPackageId: string,
    modules?: string[],
  ): Promise<any> => {
    return (await authFetch(
      `/api/billing/${orgId}/change-package`,
      "change package",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          new_package_id: newPackageId,
          ...(modules && modules.length > 0 ? { modules } : {}),
        }),
      },
    )) as any;
  };

  return changePackage;
};

// Get Customer Portal URL
export const useGetCustomerPortalUrl = () => {
  const authFetch = useAuthFetch();

  const getCustomerPortalUrl = async (
    orgId: string,
    returnUrl?: string,
  ): Promise<any> => {
    const params = new URLSearchParams();
    if (returnUrl) params.append("return_url", returnUrl);
    const query = params.toString() ? `?${params.toString()}` : "";
    return (await authFetch(
      `/api/billing/${orgId}/customer-portal${query}`,
      "get customer portal URL",
    )) as any;
  };

  return getCustomerPortalUrl;
};

// Debug Reset Subscription
export const useDebugResetSubscription = () => {
  const authFetch = useAuthFetch();

  const debugResetSubscription = async (orgId: string): Promise<any> => {
    return (await authFetch(
      `/api/billing/${orgId}/debug-reset-subscription`,
      "debug reset subscription",
      {
        method: "POST",
      },
    )) as any;
  };

  return debugResetSubscription;
};

/*
  Organization Integration APIs (Two-Tier Architecture)
  - Organization level: Define and configure integrations (credentials, connection config)
  - Workspace level: Select which integrations to use and configure workspace-specific settings
*/

// List Organization Integrations
export const useListOrganizationIntegrations = () => {
  const authFetch = useAuthFetch();

  const listOrganizationIntegrations = useCallback(
    async (
      orgId: string,
      options?: { integrationType?: string; includeInactive?: boolean },
    ): Promise<OrganizationIntegrationList> => {
      const params = new URLSearchParams();
      if (options?.integrationType)
        params.append("integration_type", options.integrationType);
      if (options?.includeInactive) params.append("include_inactive", "true");
      const query = params.toString() ? `?${params.toString()}` : "";

      return (await authFetch(
        `/api/organization/${orgId}/integrations${query}`,
        "list organization integrations",
      )) as OrganizationIntegrationList;
    },
    [authFetch],
  );

  return listOrganizationIntegrations;
};

// Get Organization Integration
export const useGetOrganizationIntegration = () => {
  const authFetch = useAuthFetch();

  const getOrganizationIntegration = useCallback(
    async (
      orgId: string,
      integrationId: string,
    ): Promise<OrganizationIntegration> => {
      return (await authFetch(
        `/api/organization/${orgId}/integrations/${integrationId}`,
        "get organization integration",
      )) as OrganizationIntegration;
    },
    [authFetch],
  );

  return getOrganizationIntegration;
};

// Create Organization Integration
export const useCreateOrganizationIntegration = () => {
  const authFetch = useAuthFetch();

  const createOrganizationIntegration = async (
    orgId: string,
    data: OrganizationIntegrationCreate,
  ): Promise<OrganizationIntegration> => {
    return (await authFetch(
      `/api/organization/${orgId}/integrations`,
      "create organization integration",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    )) as OrganizationIntegration;
  };

  return createOrganizationIntegration;
};

// Update Organization Integration
export const useUpdateOrganizationIntegration = () => {
  const authFetch = useAuthFetch();

  const updateOrganizationIntegration = async (
    orgId: string,
    integrationId: string,
    data: OrganizationIntegrationUpdate,
  ): Promise<OrganizationIntegration> => {
    return (await authFetch(
      `/api/organization/${orgId}/integrations/${integrationId}`,
      "update organization integration",
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    )) as OrganizationIntegration;
  };

  return updateOrganizationIntegration;
};

// Delete Organization Integration
export const useDeleteOrganizationIntegration = () => {
  const authFetch = useAuthFetch();

  const deleteOrganizationIntegration = async (
    orgId: string,
    integrationId: string,
    force?: boolean,
  ): Promise<void> => {
    const params = force ? "?force=true" : "";
    await authFetch(
      `/api/organization/${orgId}/integrations/${integrationId}${params}`,
      "delete organization integration",
      { method: "DELETE" },
    );
  };

  return deleteOrganizationIntegration;
};

export const useReadOrganizationIntegrationDisableImpact = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      integrationKey: string,
    ): Promise<OrganizationIntegrationDisableImpact> => {
      return (await authFetch(
        `/api/admin/integrations/${encodeURIComponent(integrationKey)}/disable-impact`,
        "read integration disable impact",
      )) as OrganizationIntegrationDisableImpact;
    },
    [authFetch],
  );
};

export const useTestDeltekConnection = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      orgId: string,
      integrationId: string,
    ): Promise<DeltekConnectionTestRead> => {
      return (await authFetch(
        `/api/organization/${encodeURIComponent(orgId)}/integrations/${encodeURIComponent(
          integrationId,
        )}/deltek/metadata`,
        "test Deltek connection",
      )) as DeltekConnectionTestRead;
    },
    [authFetch],
  );
};

const substrateEndpointCandidates = (
  orgId: string,
  integrationId: string,
  itemId?: string,
) => {
  const suffix = itemId ? `/${itemId}` : "";
  return [
    `/api/organization/${orgId}/integrations/${integrationId}/substrate-items${suffix}`,
    `/api/organizations/${orgId}/integrations/${integrationId}/substrate-items${suffix}`,
  ];
};

const normalizeSubstrateItem = (
  item: any,
): OrganizationIntegrationSubstrateItem => ({
  ...item,
  config: item?.config || item?.config_json || {},
});

// List Organization Integration Substrate Items
export const useListOrganizationIntegrationSubstrateItems = () => {
  const authFetch = useAuthFetch();

  const listOrganizationIntegrationSubstrateItems = useCallback(
    async (
      orgId: string,
      integrationId: string,
      workspaceId?: string,
    ): Promise<OrganizationIntegrationSubstrateItem[]> => {
      const querySuffix = workspaceId
        ? `?workspace_id=${encodeURIComponent(workspaceId)}`
        : "";
      const candidates = substrateEndpointCandidates(orgId, integrationId).map(
        (endpoint) => `${endpoint}${querySuffix}`,
      );
      let lastError: any = null;

      for (const endpoint of candidates) {
        try {
          const response = await authFetch(
            endpoint,
            "list organization integration substrate items",
          );
          const items = Array.isArray(response)
            ? response
            : response?.items || response?.substrate_items || [];
          return Array.isArray(items) ? items.map(normalizeSubstrateItem) : [];
        } catch (error: any) {
          lastError = error;
          if (error?.status !== 404) {
            throw error;
          }
        }
      }

      throw lastError;
    },
    [authFetch],
  );

  return listOrganizationIntegrationSubstrateItems;
};

// Create Organization Integration Substrate Item
export const useCreateOrganizationIntegrationSubstrateItem = () => {
  const authFetch = useAuthFetch();

  const createOrganizationIntegrationSubstrateItem = useCallback(
    async (
      orgId: string,
      integrationId: string,
      data: OrganizationIntegrationSubstrateItemCreate,
    ): Promise<OrganizationIntegrationSubstrateItem> => {
      const response = await authFetch(
        substrateEndpointCandidates(orgId, integrationId)[0],
        "create organization integration substrate item",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );
      return normalizeSubstrateItem(response);
    },
    [authFetch],
  );

  return createOrganizationIntegrationSubstrateItem;
};

// Update Organization Integration Substrate Item
export const useUpdateOrganizationIntegrationSubstrateItem = () => {
  const authFetch = useAuthFetch();

  const updateOrganizationIntegrationSubstrateItem = useCallback(
    async (
      orgId: string,
      integrationId: string,
      itemId: string,
      data: OrganizationIntegrationSubstrateItemUpdate,
    ): Promise<OrganizationIntegrationSubstrateItem> => {
      const response = await authFetch(
        substrateEndpointCandidates(orgId, integrationId, itemId)[0],
        "update organization integration substrate item",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );
      return normalizeSubstrateItem(response);
    },
    [authFetch],
  );

  return updateOrganizationIntegrationSubstrateItem;
};

// Delete Organization Integration Substrate Item
export const useDeleteOrganizationIntegrationSubstrateItem = () => {
  const authFetch = useAuthFetch();

  const deleteOrganizationIntegrationSubstrateItem = useCallback(
    async (
      orgId: string,
      integrationId: string,
      itemId: string,
    ): Promise<void> => {
      await authFetch(
        substrateEndpointCandidates(orgId, integrationId, itemId)[0],
        "delete organization integration substrate item",
        { method: "DELETE" },
      );
    },
    [authFetch],
  );

  return deleteOrganizationIntegrationSubstrateItem;
};

export const useGetSlackAuthorizationUrl = () => {
  const authFetch = useAuthFetch();

  const getSlackAuthorizationUrl = useCallback(
    async (
      orgIntegrationId: string,
      returnTo?: string,
    ): Promise<{ authorization_url: string }> => {
      const params = new URLSearchParams({
        org_integration_id: orgIntegrationId,
      });
      if (returnTo) {
        params.set("return_to", returnTo);
      }

      return (await authFetch(
        `/api/oauth/slack/auth?${params.toString()}`,
        "get slack authorization url",
      )) as { authorization_url: string };
    },
    [authFetch],
  );

  return getSlackAuthorizationUrl;
};

export const useGetIntegrationOAuthAuthorizationUrl = () => {
  const getAuthHeaders = useAuthHeaders();

  const getIntegrationOAuthAuthorizationUrl = useCallback(
    async (
      integrationKey: string,
      options: {
        workspaceId?: string;
        orgIntegrationId?: string;
        returnTo?: string;
      },
    ): Promise<{
      authorization_url: string;
      authorize_url?: string | null;
    }> => {
      const params = new URLSearchParams();
      if (options.workspaceId) {
        params.set("workspace_id", options.workspaceId);
      }
      if (options.orgIntegrationId) {
        params.set("org_integration_id", options.orgIntegrationId);
      }
      if (options.returnTo) {
        params.set("return_to", options.returnTo);
      }

      const headers = await getAuthHeaders();
      const response = await fetch(
        `/api/integrations/${encodeURIComponent(
          integrationKey,
        )}/oauth/authorize_url?${params.toString()}`,
        {
          headers,
          credentials: "include",
        },
      );

      if (!response.ok) {
        let errorDetail = "Failed to get integration OAuth authorization URL";
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          const errorBody = await response.json();
          if (typeof errorBody?.detail === "string") {
            errorDetail = errorBody.detail;
          }
        }
        throw new Error(errorDetail);
      }

      return (await response.json()) as {
        authorization_url: string;
        authorize_url?: string | null;
      };
    },
    [getAuthHeaders],
  );

  return getIntegrationOAuthAuthorizationUrl;
};

export const useGetDeltekOAuthAuthorizationUrl = () => {
  const getAuthHeaders = useAuthHeaders();

  return useCallback(
    async (
      workspaceId: string,
      organizationIntegrationId: string,
      returnTo: string,
    ): Promise<{ authorization_url: string }> => {
      const params = new URLSearchParams({
        workspace_id: workspaceId,
        org_integration_id: organizationIntegrationId,
        return_to: returnTo,
      });
      const response = await fetch(
        `/api/oauth/deltek/auth?${params.toString()}`,
        {
          headers: await getAuthHeaders(),
          credentials: "include",
        },
      );

      if (!response.ok) {
        let errorDetail = "Failed to start Deltek account connection";
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          const errorBody = await response.json();
          if (typeof errorBody?.detail === "string") {
            errorDetail = errorBody.detail;
          }
        }
        throw new Error(errorDetail);
      }

      return (await response.json()) as { authorization_url: string };
    },
    [getAuthHeaders],
  );
};

// Toggle Permission Verification for an Org Integration (org-admin only, sp_site only)
export const useToggleIntegrationPermissionVerification = () => {
  const authFetch = useAuthFetch();

  const toggleIntegrationPermissionVerification = async (
    orgId: string,
    integrationId: string,
    enabled: boolean,
  ): Promise<OrganizationIntegration> => {
    return (await authFetch(
      `/api/organization/${orgId}/integrations/${integrationId}/permission-verification`,
      "toggle integration permission verification",
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      },
    )) as OrganizationIntegration;
  };

  return toggleIntegrationPermissionVerification;
};

// List Available Integrations for Workspace
export const useListAvailableIntegrations = () => {
  const authFetch = useAuthFetch();

  const listAvailableIntegrations = useCallback(
    async (workspaceId: string): Promise<AvailableIntegration[]> => {
      return (await authFetch(
        `/api/workspace/integrations/available?workspace_id=${workspaceId}`,
        "list available integrations",
      )) as AvailableIntegration[];
    },
    [authFetch],
  );

  return listAvailableIntegrations;
};

// List Workspace Integration Selections
export const useListWorkspaceIntegrationSelections = () => {
  const authFetch = useAuthFetch();

  const listWorkspaceIntegrationSelections = useCallback(
    async (
      workspaceId: string,
      enabledOnly?: boolean,
    ): Promise<WorkspaceIntegrationSelection[]> => {
      const params = enabledOnly ? "enabled_only=true&" : "";
      return (await authFetch(
        `/api/workspace/integrations/selections?${params}workspace_id=${workspaceId}`,
        "list workspace integration selections",
      )) as WorkspaceIntegrationSelection[];
    },
    [authFetch],
  );

  return listWorkspaceIntegrationSelections;
};

// Get Workspace Integration Selection
export const useGetWorkspaceIntegrationSelection = () => {
  const authFetch = useAuthFetch();

  const getWorkspaceIntegrationSelection = useCallback(
    async (
      workspaceId: string,
      selectionId: string,
    ): Promise<WorkspaceIntegrationSelection> => {
      return (await authFetch(
        `/api/workspace/integrations/selections/${selectionId}?workspace_id=${workspaceId}`,
        "get workspace integration selection",
      )) as WorkspaceIntegrationSelection;
    },
    [authFetch],
  );

  return getWorkspaceIntegrationSelection;
};

// Create Workspace Integration Selection
export const useCreateWorkspaceIntegrationSelection = () => {
  const authFetch = useAuthFetch();

  const createWorkspaceIntegrationSelection = async (
    workspaceId: string,
    data: WorkspaceIntegrationSelectionCreate,
  ): Promise<WorkspaceIntegrationSelection> => {
    return (await authFetch(
      `/api/workspace/integrations/selections?workspace_id=${workspaceId}`,
      "create workspace integration selection",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    )) as WorkspaceIntegrationSelection;
  };

  return createWorkspaceIntegrationSelection;
};

// Update Workspace Integration Selection
export const useUpdateWorkspaceIntegrationSelection = () => {
  const authFetch = useAuthFetch();

  const updateWorkspaceIntegrationSelection = async (
    workspaceId: string,
    selectionId: string,
    data: WorkspaceIntegrationSelectionUpdate,
  ): Promise<WorkspaceIntegrationSelection> => {
    return (await authFetch(
      `/api/workspace/integrations/selections/${selectionId}?workspace_id=${workspaceId}`,
      "update workspace integration selection",
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    )) as WorkspaceIntegrationSelection;
  };

  return updateWorkspaceIntegrationSelection;
};

// Delete Workspace Integration Selection
export const useDeleteWorkspaceIntegrationSelection = () => {
  const authFetch = useAuthFetch();

  const deleteWorkspaceIntegrationSelection = async (
    workspaceId: string,
    selectionId: string,
  ): Promise<void> => {
    await authFetch(
      `/api/workspace/integrations/selections/${selectionId}?workspace_id=${workspaceId}`,
      "delete workspace integration selection",
      { method: "DELETE" },
    );
  };

  return deleteWorkspaceIntegrationSelection;
};

const normalizeBKGAvailableSources = (response: any): BKGAvailableSource[] => {
  const rawSources = Array.isArray(response)
    ? response
    : response?.sources || response?.items || response?.available_sources || [];

  if (!Array.isArray(rawSources)) return [];

  return rawSources
    .map((source) => ({
      workspace_id: String(source.workspace_id || source.id || ""),
      name: String(source.name || source.workspace_name || ""),
    }))
    .filter((source) => source.workspace_id && source.name);
};

// List BKG source workspaces available to a consumer workspace.
export const useListBKGAvailableSources = () => {
  const authFetch = useAuthFetch();

  const listBKGAvailableSources = useCallback(
    async (workspaceId: string): Promise<BKGAvailableSource[]> => {
      try {
        const response = await authFetch(
          `/api/workspace/bkg/available-sources?workspace_id=${workspaceId}`,
          "fetch BKG available sources",
          {
            headers: {
              "X-Workspace-Id": workspaceId,
            },
          },
        );
        return normalizeBKGAvailableSources(response);
      } catch (error: any) {
        if (
          process.env.NODE_ENV === "development" &&
          (error?.status === 404 || error?.message?.includes("404"))
        ) {
          return [
            {
              workspace_id: "mock-bkg-workspace",
              name: "Mock BKG workspace",
            },
          ];
        }
        throw error;
      }
    },
    [authFetch],
  );

  return listBKGAvailableSources;
};

// Trigger Integration Sync
export const useTriggerIntegrationSync = () => {
  const authFetch = useAuthFetch();

  const triggerIntegrationSync = async (
    workspaceId: string,
    selectionId: string,
  ): Promise<WorkspaceIntegrationSelection> => {
    return (await authFetch(
      `/api/workspace/integrations/selections/${selectionId}/sync?workspace_id=${workspaceId}`,
      "trigger integration sync",
      { method: "POST" },
    )) as WorkspaceIntegrationSelection;
  };

  return triggerIntegrationSync;
};

export interface SharePointSyncResult {
  new: number;
  updated: number;
  deleted: number;
  failed: number;
  unchanged: number;
  errors: string[];
  skipped?: boolean;
}

export interface SharePointDisconnectPreview {
  document_count: number;
}

export const usePreviewSharePointDisconnect = () => {
  const authFetch = useAuthFetch();

  return async (workspaceId: string): Promise<SharePointDisconnectPreview> => {
    return (await authFetch(
      `/api/workspace/ida/documents/sharepoint-sync/preview-disconnect?workspace_id=${workspaceId}`,
      "preview SharePoint disconnect",
    )) as SharePointDisconnectPreview;
  };
};

// Run SharePoint sync directly (synchronous — returns errors immediately)
export const useRunSharePointSync = () => {
  const authFetch = useAuthFetch();

  const runSharePointSync = async (
    workspaceId: string,
  ): Promise<SharePointSyncResult> => {
    return (await authFetch(
      `/api/workspace/ida/documents/sharepoint-sync?workspace_id=${workspaceId}`,
      "run SharePoint sync",
      { method: "POST" },
      60000,
    )) as SharePointSyncResult;
  };

  return runSharePointSync;
};

// Permission Verification Settings
export const useGetPermissionVerificationStatus = () => {
  const authFetch = useAuthFetch();

  const getPermissionVerificationStatus = useCallback(
    async (workspaceId: string): Promise<PermissionVerificationStatus> => {
      return (await authFetch(
        `/api/workspace/${workspaceId}/settings/permission-verification`,
        "get permission verification status",
      )) as PermissionVerificationStatus;
    },
    [authFetch],
  );

  return getPermissionVerificationStatus;
};

export const useTriggerPermissionSync = () => {
  const authFetch = useAuthFetch();

  const triggerPermissionSync = useCallback(
    async (
      workspaceId: string,
      selectionId: string,
    ): Promise<PermissionSyncStatusRead> => {
      return (await authFetch(
        `/api/workspace/integrations/selections/${selectionId}/sync-permissions?workspace_id=${workspaceId}`,
        "trigger permission sync",
        { method: "POST" },
      )) as PermissionSyncStatusRead;
    },
    [authFetch],
  );

  return triggerPermissionSync;
};

export const useGetPermissionSyncStatus = () => {
  const authFetch = useAuthFetch();

  const getPermissionSyncStatus = useCallback(
    async (
      workspaceId: string,
      selectionId: string,
    ): Promise<PermissionSyncStatusRead> => {
      return (await authFetch(
        `/api/workspace/integrations/selections/${selectionId}/sync-permissions?workspace_id=${workspaceId}`,
        "get permission sync status",
      )) as PermissionSyncStatusRead;
    },
    [authFetch],
  );

  return getPermissionSyncStatus;
};

// ==================== Organization Branding APIs ====================

export const useGetOrgBranding = () => {
  const authFetch = useAuthFetch();

  const getOrgBranding = useCallback(
    async (orgId: string): Promise<OrgBrandingResponse> => {
      const data = (await authFetch(
        `/api/organization/${orgId}/branding`,
        "get org branding",
      )) as OrgBrandingResponse;
      const apiBase = process.env.REACT_APP_API_URL || "";
      return {
        ...data,
        logo_light_url: data.logo_light_url
          ? `${apiBase}${data.logo_light_url}`
          : null,
        logo_dark_url: data.logo_dark_url
          ? `${apiBase}${data.logo_dark_url}`
          : null,
      };
    },
    [authFetch],
  );

  return getOrgBranding;
};

export const useUpdateOrgBranding = () => {
  const authFetch = useAuthFetch();

  const updateOrgBranding = useCallback(
    async (
      orgId: string,
      themeConfig: OrgThemeConfig,
    ): Promise<OrgBrandingResponse> => {
      return (await authFetch(
        `/api/organization/${orgId}/branding`,
        "update org branding",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ theme_config: themeConfig }),
        },
      )) as OrgBrandingResponse;
    },
    [authFetch],
  );

  return updateOrgBranding;
};

export const useResetOrgBranding = () => {
  const authFetch = useAuthFetch();

  const resetOrgBranding = useCallback(
    async (orgId: string): Promise<void> => {
      await authFetch(
        `/api/organization/${orgId}/branding`,
        "reset org branding",
        {
          method: "DELETE",
        },
      );
    },
    [authFetch],
  );

  return resetOrgBranding;
};

export const useUploadOrgLogo = () => {
  const authFetch = useAuthFetch();

  const uploadOrgLogo = useCallback(
    async (
      orgId: string,
      mode: "light" | "dark",
      file: File,
    ): Promise<{ logo_url: string }> => {
      const formData = new FormData();
      formData.append("file", file);
      return (await authFetch(
        `/api/organization/${orgId}/branding/logo/${mode}`,
        "upload org logo",
        { method: "POST", body: formData },
      )) as { logo_url: string };
    },
    [authFetch],
  );

  return uploadOrgLogo;
};

export const useDeleteOrgLogo = () => {
  const authFetch = useAuthFetch();

  const deleteOrgLogo = useCallback(
    async (orgId: string, mode: "light" | "dark"): Promise<void> => {
      await authFetch(
        `/api/organization/${orgId}/branding/logo/${mode}`,
        "delete org logo",
        { method: "DELETE" },
      );
    },
    [authFetch],
  );

  return deleteOrgLogo;
};

// Re-export the useAuthFetch hook so it can be imported from services/api
export { useAuthFetch };
